import HoverPrefetchLink from '@/components/hover-prefetch-link';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverAnchor, PopoverArrow, PopoverContent } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { websiteEnv } from '@/constants/website-env';
import useIsMobile from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import { isProductConfigurable } from '@/util/isProductConfigurable';
import clsx from 'clsx';
import Decimal from 'decimal.js';
import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { OptionsRoot, SwatchDataValue } from '../../types/data';
import type { ConfigurableOption, Item, ProductRoot } from '../../types/product';

type ColorOptionProps = {
  item: Item[] | ConfigurableOption[];
  hasOnlyOneColor: boolean;
  options: OptionsRoot | ConfigurableOption[];
  categoryUrlKey: string;
  outOfStockVariants: any;
  product: ProductRoot;
  showShopMoreInThisColor?: boolean;
  maybeTabs?: React.ReactNode;
  optionSpecs: any;
  clickIndex: number;
  handleClick: any;
  hasAvailableProduct: boolean;
  setSwatchAddToCartDialogVisible?: (nextVisible: boolean) => void;
  showSwatchAddToCartTrigger?: boolean;
  getIsOutOfStock: (formField: string, valueIndex: string) => boolean;
  onResetAvailableProductOtherAttr?: (nextColorValue: any) => void;
  setRelatedProductFilterColor?: Dispatch<SetStateAction<string>>;
  crossPrice?: number | null;
};

export const CustomizeDressColorOptions = ({
  item,
  hasOnlyOneColor,
  options,
  categoryUrlKey,
  outOfStockVariants,
  product,
  showShopMoreInThisColor = true,
  maybeTabs,
  handleClick,
  clickIndex,
  optionSpecs,
  hasAvailableProduct,
  setSwatchAddToCartDialogVisible,
  showSwatchAddToCartTrigger = false,
  getIsOutOfStock,
  onResetAvailableProductOtherAttr,
  setRelatedProductFilterColor,
  crossPrice,
}: ColorOptionProps) => {
  const fabricName = item?.label.split('-')[1];
  const form = useFormContext();
  const { watch, setValue } = form;
  const selectedFabric = watch('Fabric');
  const allFormData = watch();

  const isMobile = useIsMobile();

  const [mobileColorsModalVisible, setMobileColorsModalVisible] = useState(false);

  const oldValues = item?.values;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const variants = product?.variants || [];
  // 找到当前 color 的所有 variants
  const findVariants = useCallback(
    (item_value_index: number) => {
      const match =
        variants &&
        variants.filter(variant => {
          const { attributes } = variant || {};
          const { value_index: variant_value_index } = attributes[0] || {};

          return variant_value_index === item_value_index;
        });

      return match;
    },
    [variants],
  );

  // 将 outofstock 的放在后面
  const values = useMemo(() => {
    if (!isProductConfigurable(product)) {
      return oldValues;
    }

    const instockArray: any[] = [];
    const outofStockArray: any[] = [];

    oldValues?.forEach((item: any) => {
      const { value_index: item_value_index } = item || {};

      const match = findVariants(item_value_index);

      const allOutOfStock = match?.every((item: any) => item.product.stock_status === 'OUT_OF_STOCK');

      if (allOutOfStock) {
        outofStockArray.push(item);
      } else {
        instockArray.push(item);
      }
    });

    if (hasAvailableProduct) {
      return [...instockArray];
    }

    return [...instockArray, ...outofStockArray];
  }, [findVariants, hasAvailableProduct, oldValues, product]);

  if (
    !hasOnlyOneColor &&
    !(
      selectedFabric &&
      options
        .find(d => d.label === 'Fabric')
        ?.values.find(v => v.value_index.toString() === selectedFabric?.value_index)
        ?.label.includes(fabricName)
    )
  ) {
    return null;
  }

  const selectedColorItem = allFormData?.Color;
  const colorPrice = selectedColorItem?.price;
  const crossPricePercent =
    websiteEnv.isGermanyEnglish && !!crossPrice && typeof colorPrice === 'number' && colorPrice < 0
      ? Decimal(Math.abs(colorPrice)).div(crossPrice).mul(100).toFixed(0)
      : null;
  const hasCrossPriceDiscountPercentage = !!crossPricePercent && parseInt(crossPricePercent) > 0;

  const showAllColors = () => {
    setMobileColorsModalVisible(true);
  };

  return (
    <>
      <FormField
        key={item?.uid}
        control={form.control}
        name={'Color'}
        render={({ field }) => {
          return (
            <div>
              {maybeTabs}
              <FormItem className={clsx('bg-white px-2.5 py-[15px] lg_p-0')}>
                {!isMobile && (
                  <FormLabel
                    className='mb-2.5 flex flex-wrap items-center justify-between gap-1 '
                    id='Color'
                  >
                    <div className='flex items-center gap-x-2.5'>
                      <span className='text-base font-medium uppercase leading-5 lg_font-normal'>
                        {websiteEnv.isUnitedKingdom ? 'Colour' : 'Color'}:{' '}
                      </span>
                      <span className=' text-[15px] leading-5 text-#403732 lg_text-80583F'>
                        {websiteEnv.isUnitedKingdom && selectedColorItem?.label === 'Multi Color'
                          ? 'Multicolour'
                          : selectedColorItem?.label}
                      </span>
                      {(colorPrice < 0 && !websiteEnv.isGermanyEnglish) ||
                      (hasCrossPriceDiscountPercentage && websiteEnv.isGermanyEnglish) ? (
                        <div className='flex items-center'>
                          <div
                            className={clsx(
                              'h-0 w-0 ',
                              'border-b-[8px] border-t-[8px] border-b-transparent border-t-transparent',
                              'border-r-[6px] border-r-e73635',
                            )}
                          />
                          <div className='bg-e73635 py-0.5 pl-0.5 pr-[3px] text-xs font-medium leading-none text-white'>
                            {websiteEnv.isGermanyEnglish ? (
                              `-${crossPricePercent}%`
                            ) : (
                              <>
                                {colorPrice}
                                {process.env.NEXT_PUBLIC_DEFAULT_DISPLAY_CURRENCY_CODE}
                              </>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {showShopMoreInThisColor && selectedColorItem?.label && (
                      <HoverPrefetchLink
                        href={`/${categoryUrlKey}/color/${selectedColorItem?.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className='text-[15px] leading-5 underline lg_hover_text-80583F lg_hover_decoration-80583F'
                      >
                        Shop More In This {websiteEnv.isUnitedKingdom ? 'Colour' : 'Color'}
                      </HoverPrefetchLink>
                    )}
                  </FormLabel>
                )}
                <FormMessage />
                <FormControl>
                  {isMobile ? (
                    <RadioGroup
                      suppressHydrationWarning
                      onValueChange={value => {
                        const selectedOption = values.find(v => v.value_index.toString() === value);

                        onResetAvailableProductOtherAttr?.({
                          value_index: value,
                          label: selectedOption?.label,
                          price: selectedOption?.price,
                          option_id: selectedOption?.option_id?.toString() || item?.attribute_id,
                        });
                        setRelatedProductFilterColor?.(selectedOption?.label || '');

                        if (selectedOption) {
                          field.onChange({
                            value_index: value,
                            label: selectedOption?.label,
                            price: selectedOption?.price,
                            option_id: selectedOption?.option_id?.toString() || item?.attribute_id,
                          });
                          form.trigger('Color');
                        }

                        const shouldAddToSpec = optionSpecs.includes(selectedOption?.value_index.toString());

                        if (shouldAddToSpec) {
                          handleClick({
                            bool: true,
                            text: selectedOption?.value_index?.toString(),
                            index: clickIndex,
                          });
                        } else {
                          handleClick({
                            bool: true,
                            text: '',
                            index: clickIndex,
                          });
                        }
                      }}
                      value={field.value?.value_index}
                      className={cn(
                        'flex flex-wrap justify-between gap-y-[5px]',
                        values.length > 3 ? 'justify-start' : 'justify-center',
                      )}
                    >
                      {values.slice(0, 6).map(option => (
                        <ColorRadioItem
                          key={option.uid}
                          item={option}
                          outOfStockVariants={outOfStockVariants}
                          optionSpecs={optionSpecs}
                          field={field}
                          hasAvailableProduct={hasAvailableProduct}
                          isOutOfStock={getIsOutOfStock('Color', option.value_index)}
                        />
                      ))}

                      {values.length > 6 && (
                        <div className='p-[4px]'>
                          <div
                            onClick={showAllColors}
                            className='h-8 w-8'
                          >
                            <svg
                              width='32'
                              height='32'
                              viewBox='0 0 32 32'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <rect
                                width='32'
                                height='32'
                                rx='16'
                                fill='#F8F3EF'
                              />
                              <path
                                fillRule='evenodd'
                                clipRule='evenodd'
                                d='M15 26L15 6L17 6L17 26L15 26Z'
                                fill='#80583F'
                              />
                              <path
                                fillRule='evenodd'
                                clipRule='evenodd'
                                d='M26 17H6V15H26V17Z'
                                fill='#80583F'
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </RadioGroup>
                  ) : (
                    <RadioGroup
                      suppressHydrationWarning
                      onValueChange={value => {
                        const selectedOption = values.find(v => v.value_index.toString() === value);

                        onResetAvailableProductOtherAttr?.({
                          value_index: value,
                          label: selectedOption?.label,
                          price: selectedOption?.price,
                          option_id: selectedOption?.option_id?.toString() || item?.attribute_id,
                        });
                        setRelatedProductFilterColor?.(selectedOption?.label || '');

                        if (selectedOption) {
                          field.onChange({
                            value_index: value,
                            label: selectedOption?.label,
                            price: selectedOption?.price,
                            option_id: selectedOption?.option_id?.toString() || item?.attribute_id,
                          });
                          form.trigger('Color');
                        }

                        const shouldAddToSpec = optionSpecs.includes(selectedOption?.value_index.toString());

                        if (shouldAddToSpec) {
                          handleClick({
                            bool: true,
                            text: selectedOption?.value_index?.toString(),
                            index: clickIndex,
                          });
                        } else {
                          handleClick({
                            bool: true,
                            text: '',
                            index: clickIndex,
                          });
                        }
                      }}
                      value={field.value?.value_index}
                      className='flex flex-wrap gap-x-0.5 gap-y-[5px]'
                    >
                      {values.map(option => (
                        <ColorRadioItem
                          key={option.uid}
                          item={option}
                          outOfStockVariants={outOfStockVariants}
                          optionSpecs={optionSpecs}
                          field={field}
                          hasAvailableProduct={hasAvailableProduct}
                          isOutOfStock={getIsOutOfStock('Color', option.value_index)}
                        />
                      ))}
                    </RadioGroup>
                  )}
                </FormControl>
              </FormItem>

              {mobileColorsModalVisible && (
                <div
                  onClick={() => {
                    setMobileColorsModalVisible(false);
                  }}
                  className='fixed left-0 top-0 z-10 h-full w-full bg-black bg-opacity-50'
                ></div>
              )}
              {isMobile && (
                <FormItem>
                  <FormControl>
                    <div
                      className={cn(
                        'fixed bottom-0 left-0 z-[100] flex w-full flex-col bg-white px-[10px] py-[20px]',
                        mobileColorsModalVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0',
                      )}
                    >
                      <div className='mb-[10px] flex items-center justify-start'>
                        <span className='font-jost text-[17px] font-[500] uppercase leading-[1.4] text-[#403732]'>
                          Color
                        </span>
                        <span className='ml-[10px] font-jost text-[15px] text-[#403732]'>
                          {selectedColorItem?.label}
                        </span>
                      </div>
                      <RadioGroup
                        suppressHydrationWarning
                        onValueChange={value => {
                          const selectedOption = values.find(v => v.value_index.toString() === value);

                          onResetAvailableProductOtherAttr?.({
                            value_index: value,
                            label: selectedOption?.label,
                            price: selectedOption?.price,
                            option_id: selectedOption?.option_id?.toString() || item?.attribute_id,
                          });
                          setRelatedProductFilterColor?.(selectedOption?.label || '');

                          if (selectedOption) {
                            field.onChange({
                              value_index: value,
                              label: selectedOption?.label,
                              price: selectedOption?.price,
                              option_id: selectedOption?.option_id?.toString() || item?.attribute_id,
                            });
                            form.trigger('Color');
                          }

                          const shouldAddToSpec = optionSpecs.includes(selectedOption?.value_index.toString());

                          if (shouldAddToSpec) {
                            handleClick({
                              bool: true,
                              text: selectedOption?.value_index?.toString(),
                              index: clickIndex,
                            });
                          } else {
                            handleClick({
                              bool: true,
                              text: '',
                              index: clickIndex,
                            });
                          }
                        }}
                        value={field.value?.value_index}
                        className='flex flex-wrap gap-x-0.5 gap-y-[5px]'
                      >
                        {values.map(option => (
                          <ColorRadioItem
                            key={option.uid}
                            item={option}
                            outOfStockVariants={outOfStockVariants}
                            optionSpecs={optionSpecs}
                            field={field}
                            hasAvailableProduct={hasAvailableProduct}
                            isOutOfStock={getIsOutOfStock('Color', option.value_index)}
                          />
                        ))}
                      </RadioGroup>

                      <div
                        onClick={() => {
                          setMobileColorsModalVisible(false);
                        }}
                        className='mt-[20px] h-[45px] w-full rounded-[3px] bg-primary text-center text-[15px] font-[500] uppercase leading-[45px] text-white'
                      >
                        Continue
                      </div>
                    </div>
                  </FormControl>
                </FormItem>
              )}
            </div>
          );
        }}
      />
    </>
  );
};

const ColorRadioItem = ({
  item,
  outOfStockVariants,
  optionSpecs,
  field,
  hasAvailableProduct,
  isOutOfStock,
}: {
  item: SwatchDataValue;
  outOfStockVariants: any;
  optionSpecs: any;
  field: any;
  hasAvailableProduct: boolean;
  isOutOfStock: boolean;
}) => {
  // const inStock = optionSpecs.includes(item.value_index.toString());

  // const isOutOfStock = outOfStockVariants.flat().includes(item.value_index);
  const [showPopover, setShowPopover] = useState(false);
  const isMobile = useIsMobile();
  const { thumbnail, value } = item?.swatch_data || {};

  let swatchValue = '';

  // 在thumbnail存在的情况下如果thumbnail值包含#则是色值否则是图片
  if (thumbnail && thumbnail.indexOf('#') < 0) {
    if (thumbnail.indexOf('https://magento') === 0) {
      swatchValue = `url(${thumbnail.replace(process.env.NEXT_PUBLIC_MAGENTO_BACKEND_URL, '/')})`;
    } else {
      swatchValue = `url("${process.env.NEXT_PUBLIC_BASE_PATH}/media/attribute/swatch/swatch_thumb/110x90${thumbnail}")`;
    }
  } else {
    swatchValue = value as string;
  }

  const finalStyle = Object.assign(
    {},
    {
      '--venia-swatch-bg': swatchValue,
      background: swatchValue,
      backgroundSize: 'contain !important',
    },
  );

  const colorPrice = item?.price;

  const shouldShowPriceTag = typeof colorPrice === 'number' && colorPrice < 0;

  return (
    <FormItem
      className={cn('group relative flex items-center space-y-0', isOutOfStock && hasAvailableProduct && 'hidden')}
    >
      <FormControl>
        <RadioGroupItem
          value={item.value_index.toString()}
          className='peer sr-only'
          suppressHydrationWarning
        />
      </FormControl>
      <Popover open={isMobile ? false : showPopover}>
        <PopoverAnchor>
          <FormLabel
            className={cn(
              'flex cursor-pointer items-center justify-between rounded-full border border-solid border-transparent p-[4px] ',
              field.value?.value_index === item.value_index.toString() ? 'border-primary' : '',
            )}
            onMouseEnter={() => {
              setShowPopover(true);
            }}
            onMouseLeave={() => {
              setShowPopover(false);
            }}
          >
            <div
              className={cn(
                'relative h-8 w-8 rounded-full !bg-contain',
                item?.swatch_data?.value === '#ffffff' || item?.swatch_data?.value === '#FFFFFF'
                  ? 'border-1 border-E7DED5'
                  : '',
                isOutOfStock && 'opacity-50',
                isOutOfStock && hasAvailableProduct && 'hidden opacity-50',
              )}
              style={finalStyle}
              data-cy='Swatch-root'
            >
              {isOutOfStock && (
                <div className='absolute  left-1/2 top-1/2 z-[999] h-px w-full -translate-x-1/2 -translate-y-1/2 rotate-45 bg-white' />
              )}
              {shouldShowPriceTag ? (
                <div
                  className={clsx(' text-sm text-white', 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2')}
                >
                  Sale
                </div>
              ) : null}
            </div>
          </FormLabel>
        </PopoverAnchor>
        <PopoverContent
          side='top'
          sideOffset={1}
        >
          <PopoverArrow asChild>
            <div
              className={clsx(
                'absolute -bottom-[3px] left-1/2 -translate-x-1/2',
                'border-l-[4px] border-r-[4px] border-t-[6px] ',
                'border-l-transparent border-r-transparent border-t-white ',
                'before_absolute before_h-[5px] before_w-[1px] before_bg-80583F before_content-[""]',
                'before_bottom-0 before_origin-bottom  before_rotate-[34deg] ',
                'after_absolute after_h-[5px] after_w-[1px] after_bg-80583F after_content-[""]',
                'after_bottom-0 after_origin-bottom after_rotate-[-34deg] ',
              )}
            />
          </PopoverArrow>
          <div className=' w-max rounded-[3px] border-1 border-solid border-80583F bg-white px-1 text-[13px] leading-[1.4] shadow-none outline-0'>
            {item.label}
          </div>
        </PopoverContent>
      </Popover>
    </FormItem>
  );
};
